/**
 * Mock Data for Demo Mode
 * Données de test pour démonstration
 */

import type { Circuit, QuickReply } from '@/types';

export const MOCK_CIRCUITS: Circuit[] = [
  {
    id: 1,
    name: 'Trek en Islande - Terres de Feu et de Glace',
    subtitle: 'Randonnée au cœur des paysages volcaniques',
    destination: 'Islande',
    duration: 12,
    price: 2890,
    url: 'https://www.nomade-aventure.com/voyage-aventure/islande/trek-islande',
    resume:
      "Partez à la découverte de l'Islande sauvage lors de ce trek exceptionnel. Des geysers bouillonnants aux glaciers majestueux, en passant par les cascades spectaculaires et les plages de sable noir, ce voyage vous emmène au cœur d'une nature époustouflante. Randonnées quotidiennes dans des paysages à couper le souffle, nuits en refuges de montagne, et rencontres authentiques avec la culture islandaise.",
    programme:
      "Jour 1-2: Reykjavik et Cercle d'Or\nJour 3-5: Landmannalaugar et montagnes colorées\nJour 6-8: Traversée du Laugavegur\nJour 9-10: Þórsmörk et glacier Eyjafjallajökull\nJour 11-12: Retour via la côte sud",
    images: [
      'https://images.unsplash.com/photo-1504893524553-b855bce32c67',
      'https://images.unsplash.com/photo-1531366936337-7c912a4589a7',
      'https://images.unsplash.com/photo-1483347756197-71ef80e95f73',
    ],
    niveau: 'Modéré à Difficile',
    themes: ['Trek', 'Nature', 'Volcans', 'Glaciers'],
  },
  {
    id: 2,
    name: 'Aurores Boréales en Laponie',
    subtitle: 'Magie des lumières du Nord',
    destination: 'Finlande',
    duration: 8,
    price: 2190,
    url: 'https://www.nomade-aventure.com/voyage-aventure/finlande/aurores-boreales',
    resume:
      'Vivez une expérience unique au cœur de la Laponie finlandaise. Partez en quête des aurores boréales, ces lumières magiques qui dansent dans le ciel arctique. Entre randonnées en raquettes, safari en traîneau à chiens, et soirées au coin du feu dans des chalets traditionnels, ce voyage combine aventure et confort.',
    programme:
      'Jour 1-2: Rovaniemi et visite du village du Père Noël\nJour 3-4: Safari motoneige et traîneau à chiens\nJour 5-6: Randonnée en raquettes et observation aurores\nJour 7-8: Détente et activités optionnelles',
    images: [
      'https://images.unsplash.com/photo-1579033461380-adb47c3eb938',
      'https://images.unsplash.com/photo-1551582045-6ec9c11d8697',
      'https://images.unsplash.com/photo-1605649487212-47bdab064df7',
    ],
    niveau: 'Facile',
    themes: ['Aurores Boréales', 'Hiver', 'Traîneau', 'Nature'],
  },
  {
    id: 3,
    name: 'Désert du Sahara - Dunes et Oasis',
    subtitle: 'Aventure chamelière au Maroc',
    destination: 'Maroc',
    duration: 10,
    price: 1690,
    url: 'https://www.nomade-aventure.com/voyage-aventure/maroc/desert-sahara',
    resume:
      "Plongez dans l'immensité du désert marocain lors de ce trek à dos de chameau. Des dunes dorées de l'Erg Chebbi aux oasis verdoyantes, découvrez la magie du Sahara. Nuits sous les étoiles dans des campements bédouins, rencontres avec les nomades, et découverte de la culture berbère authentique.",
    programme:
      "Jour 1-2: Marrakech et traversée de l'Atlas\nJour 3-7: Trek chamelière dans le désert\nJour 8: Oasis de Fint et kasbahs\nJour 9-10: Retour via la vallée du Dadès",
    images: [
      'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800',
      'https://images.unsplash.com/photo-1565967511849-76a60a516170',
      'https://images.unsplash.com/photo-1551632811-561732d1e306',
    ],
    niveau: 'Modéré',
    themes: ['Désert', 'Chameau', 'Culture', 'Bivouac'],
  },
  {
    id: 4,
    name: 'Tour du Mont-Blanc Intégral',
    subtitle: 'Le trek mythique des Alpes',
    destination: 'France',
    duration: 11,
    price: 1890,
    url: 'https://www.nomade-aventure.com/voyage-aventure/france/tour-mont-blanc',
    resume:
      "Le Tour du Mont-Blanc est sans doute le trek le plus célèbre d'Europe. 170 km à travers trois pays (France, Italie, Suisse), des paysages alpins époustouflants, des cols mythiques et des refuges chaleureux. Une aventure inoubliable au pied du toit de l'Europe.",
    programme:
      'Jour 1-2: Les Houches - Les Contamines\nJour 3-4: Col du Bonhomme et Val Veni\nJour 5-7: Grand Col Ferret et Val Ferret\nJour 8-9: Champex et col de Balme\nJour 10-11: Vallée de Chamonix',
    images: [
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4',
      'https://images.unsplash.com/photo-1519904981063-b0cf448d479e',
      'https://images.unsplash.com/photo-1609137144813-7d9921338f24',
    ],
    niveau: 'Difficile',
    themes: ['Trek', 'Montagne', 'Alpes', 'Haute Altitude'],
  },
  {
    id: 5,
    name: 'Safari et Kilimandjaro - Tanzanie',
    subtitle: 'Du Serengeti au toit de l\'Afrique',
    destination: 'Tanzanie',
    duration: 16,
    price: 4590,
    url: 'https://www.nomade-aventure.com/voyage-aventure/tanzanie/kilimandjaro',
    resume:
      "Un voyage exceptionnel combinant safari dans les parcs légendaires de Tanzanie et ascension du Kilimandjaro (5895m). Observez les Big Five dans leur habitat naturel, puis relevez le défi d'atteindre le point culminant de l'Afrique. Une aventure complète alliant faune sauvage et haute montagne.",
    programme:
      'Jour 1-5: Safari Serengeti, Ngorongoro et Tarangire\nJour 6-7: Préparation et acclimatation\nJour 8-13: Ascension Kilimandjaro (voie Machame)\nJour 14-16: Descente et retour Arusha',
    images: [
      'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e',
      'https://images.unsplash.com/photo-1516426122078-c23e76319801',
      'https://images.unsplash.com/photo-1489392191049-fc10c97e64b6',
    ],
    niveau: 'Très Difficile',
    themes: ['Safari', 'Sommet', 'Faune', 'Altitude'],
  },
];

export const MOCK_QUICK_REPLIES: Record<string, QuickReply[]> = {
  greeting: [
    { id: 'qr-1', text: 'Destinations nature', icon: '🌲' },
    { id: 'qr-2', text: 'Voyages en montagne', icon: '⛰️' },
    { id: 'qr-3', text: 'Aventures exotiques', icon: '🌴' },
    { id: 'qr-4', text: 'Treks & Randonnées', icon: '🥾' },
  ],
  discovery: [
    { id: 'qr-5', text: 'Europe', icon: '🇪🇺' },
    { id: 'qr-6', text: 'Afrique', icon: '🌍' },
    { id: 'qr-7', text: 'Asie', icon: '🏔️' },
    { id: 'qr-8', text: 'Amérique', icon: '🗽' },
  ],
  circuits: [
    { id: 'qr-1', text: 'Voir mes circuits favoris', icon: '❤️' },
    { id: 'qr-2', text: 'Prendre RDV', icon: '📞' },
    { id: 'qr-3', text: 'Nouvelle discussion', icon: '🗺️' },
  ],
  booking: [
    { id: 'qr-12', text: 'Réserver maintenant', icon: '✅' },
    { id: 'qr-13', text: 'Demander un devis', icon: '💶' },
    { id: 'qr-14', text: 'Poser une question', icon: '❓' },
  ],
};

export const MOCK_AGENT_MESSAGES = [
  'Bonjour ! Je suis Anouk, votre conseillère voyage chez Nomade Aventure. Je suis là pour vous aider à trouver votre prochaine aventure. Quelle destination vous fait rêver ?',
  'L\'Islande est un choix extraordinaire ! C\'est une terre de contrastes avec ses volcans, glaciers et aurores boréales. Vous préférez plutôt la randonnée ou une expérience plus contemplative ?',
  'Parfait ! Je vous ai trouvé plusieurs circuits de trek en Islande qui correspondent à vos attentes. Regardez ces propositions.',
  'Excellent choix ! Ce trek traverse les plus beaux paysages d\'Islande. Voulez-vous en savoir plus sur l\'itinéraire détaillé ou préférez-vous que je vous trouve d\'autres options ?',
  'Je vous propose de prendre RDV avec l\'un de nos conseillers spécialistes de l\'Islande qui pourra répondre à toutes vos questions et finaliser votre réservation. Qu\'en pensez-vous ?',
];

export const MOCK_DESTINATIONS = [
  'Islande',
  'Finlande',
  'Maroc',
  'Népal',
  'Patagonie',
  'Tanzanie',
  'Nouvelle-Zélande',
];

export const MOCK_THEMES = [
  'Trek',
  'Safari',
  'Aurores Boréales',
  'Désert',
  'Montagne',
  'Jungle',
  'Volcans',
];
